import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { getOidcConfig, getOidcConfigSync } from '../config/oidcConfig';
import { AuthUser } from './auth';
import { isExternalUserError } from '../utils/authErrorDetection';

// OIDC User interface extending the base User type
export interface OidcUser extends User {
  // Additional properties if needed
}

// OIDC Authentication Service
class OidcAuthService {
  private static instance: OidcAuthService;
  private userManager: UserManager;
  private currentUser: User | null = null;
  private initializationPromise: Promise<void> | null = null;
  
  private constructor() {
    // Initialize with fallback config - will be updated asynchronously
    const initialConfig = getOidcConfigSync();
    this.userManager = new UserManager({
      ...initialConfig,
      userStore: new WebStorageStateStore({ store: window.sessionStorage })
    });
    
    // Set up event handlers
    this.setupEventHandlers();
    
    // Initialize configuration asynchronously
    this.initializationPromise = this.initializeAsync();
  }
  
  private async initializeAsync(): Promise<void> {
    try {
      const config = await getOidcConfig();
      
      // Recreate user manager with proper configuration
      this.userManager = new UserManager({
        ...config,
        userStore: new WebStorageStateStore({ store: window.sessionStorage })
      });
      
      // Re-setup event handlers
      this.setupEventHandlers();
      
      console.log('🔐 OIDC service initialized with Key Vault configuration');
    } catch (error) {
      console.error('Failed to initialize OIDC service with Key Vault config:', error);
      // Keep fallback configuration
    }
  }
  
  static getInstance(): OidcAuthService {
    if (!OidcAuthService.instance) {
      OidcAuthService.instance = new OidcAuthService();
    }
    return OidcAuthService.instance;
  }

  // Check if we're running in local development mode
  private isLocalDevelopment(): boolean {
    return (
      process.env.NODE_ENV === 'development' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.port === '3000'
    );
  }

  // Ensure the service is properly initialized before use
  public async ensureInitialized(): Promise<void> {
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
  }
  
  private setupEventHandlers(): void {
    // User loaded event
    this.userManager.events.addUserLoaded((user: User) => {
      console.log('OIDC: User loaded', user);
      this.currentUser = user;
    });
    
    // User unloaded event
    this.userManager.events.addUserUnloaded(() => {
      console.log('OIDC: User unloaded');
      this.currentUser = null;
    });
    
    // Silent renew error
    this.userManager.events.addSilentRenewError((error) => {
      console.error('OIDC: Silent renew error', error);
      // Could trigger re-authentication here
    });
    
    // Access token expiring
    this.userManager.events.addAccessTokenExpiring(() => {
      console.log('OIDC: Access token expiring');
      // Token will be automatically renewed if automaticSilentRenew is true
    });
    
    // Access token expired
    this.userManager.events.addAccessTokenExpired(() => {
      console.log('OIDC: Access token expired');
      // User needs to re-authenticate
    });
  }
  
  // Start the login process
  async login(): Promise<void> {
    try {
      console.log('OIDC: Starting login process');
      console.log('⏳ Ensuring OIDC service is fully initialized...');
      
      // Wait for Key Vault configuration to load
      await this.ensureInitialized();
      
      console.log('✅ OIDC service initialized, starting redirect');
      await this.userManager.signinRedirect();
    } catch (error) {
      console.error('OIDC: Login error', error);
      throw error;
    }
  }
  
  // Handle login callback
  async handleLoginCallback(): Promise<User | null> {
    try {
      console.log('🔄 OIDC: Starting callback processing...');
      console.log('⏳ Ensuring OIDC service is fully initialized...');
      
      // Wait for Key Vault configuration to load
      await this.ensureInitialized();
      
      console.log('✅ OIDC service initialized, proceeding with callback');
      console.log('🔗 Current URL:', window.location.href);
      console.log('🔧 UserManager settings:', {
        authority: this.userManager.settings.authority,
        client_id: this.userManager.settings.client_id,
        redirect_uri: this.userManager.settings.redirect_uri
      });
      
      const user = await this.userManager.signinRedirectCallback();
      this.currentUser = user;
      
      console.log('✅ OIDC: Login callback successful!');
      console.log('👤 User profile:', {
        sub: user.profile.sub,
        name: user.profile.name,
        email: user.profile.email,
        iss: user.profile.iss,
        aud: user.profile.aud
      });
      console.log('🎫 Token info:', {
        access_token: user.access_token ? '***' + user.access_token.slice(-10) : 'none',
        id_token: user.id_token ? '***' + user.id_token.slice(-10) : 'none',
        token_type: user.token_type,
        expires_at: user.expires_at
      });
      
      return user;
    } catch (error) {
      console.error('❌ OIDC: Login callback error', error);
      
      // Enhanced error logging
      if (error instanceof Error) {
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        
        // Check for external user access errors
        if (isExternalUserError(error)) {
          console.log('🚫 External user access attempt detected, redirecting to custom error page');
          window.location.href = '/auth-error?type=external_user';
          return null;
        }
      }
      
      // Check if it's an OIDC-specific error
      if (typeof error === 'object' && error !== null) {
        console.error('OIDC Error details:', error);
      }
      
      throw error;
    }
  }
  
  
  // Start the logout process
  async logout(): Promise<void> {
    try {
      console.log('OIDC: Starting logout process');
      await this.userManager.signoutRedirect();
    } catch (error) {
      console.error('OIDC: Logout error', error);
      throw error;
    }
  }
  
  // Handle logout callback
  async handleLogoutCallback(): Promise<void> {
    try {
      console.log('OIDC: Handling logout callback');
      console.log('🔗 Logout callback URL:', window.location.href);
      
      await this.userManager.signoutRedirectCallback();
      this.currentUser = null;
      
      // Clear all local storage and session storage
      localStorage.clear();
      sessionStorage.clear();
      
      console.log('OIDC: Logout callback successful, storage cleared');
    } catch (error) {
      console.error('OIDC: Logout callback error', error);
      
      // Even if there's an error, clear the storage and continue
      localStorage.clear();
      sessionStorage.clear();
      this.currentUser = null;
      
      console.log('OIDC: Logout completed with error, but storage cleared');
      // Don't throw the error to prevent the logout callback from failing
    }
  }
  
  // Get current user
  async getCurrentUser(): Promise<User | null> {
    try {
      await this.ensureInitialized();

      // In local development, skip OIDC and let auth.ts use mock user
      // Unless REACT_APP_FORCE_REAL_AUTH is explicitly set
      if (this.isLocalDevelopment() && process.env.REACT_APP_FORCE_REAL_AUTH !== 'true') {
        console.log('🔍 OIDC: Local dev mode - returning null to use mock user');
        return null;
      }

      if (this.currentUser) {
        console.log('🔍 OIDC: Returning cached user');
        return this.currentUser;
      }
      
      const user = await this.userManager.getUser();
      this.currentUser = user;
      console.log('🔍 OIDC getCurrentUser:', {
        hasUser: !!user,
        profile: user?.profile ? {
          email: user.profile.email,
          name: user.profile.name,
          roles: user.profile.roles
        } : null,
        hasAccessToken: !!user?.access_token,
        expired: user?.expired
      });
      return user;
    } catch (error) {
      console.error('OIDC: Get current user error', error);
      return null;
    }
  }
  
  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user !== null && !user.expired;
  }
  
  // Get access token for API calls
  async getAccessToken(): Promise<string | null> {
    await this.ensureInitialized();
    let user = await this.getCurrentUser();
    if (user?.expired) {
      user = await this.getFreshUser(user);
    }
    console.log('🔍 OIDC getAccessToken called:', {
      hasUser: !!user,
      hasAccessToken: !!user?.access_token,
      tokenType: user?.token_type,
      expiresAt: user?.expires_at,
      isExpired: user?.expired
    });
    return user?.access_token || null;
  }
  
  // Get ID token for API calls (contains user identity and roles)
  async getIdToken(): Promise<string | null> {
    await this.ensureInitialized();
    let user = await this.getCurrentUser();
    if (user?.expired) {
      user = await this.getFreshUser(user);
    }
    console.log('🔍 OIDC getIdToken called:', {
      hasUser: !!user,
      hasIdToken: !!user?.id_token,
      isExpired: user?.expired
    });
    return user?.id_token || null;
  }

  // Silently renew an expired cached user before handing its token to a caller.
  // Without this, a stale session (e.g. resumed from sessionStorage after the
  // token's lifetime elapsed, or automaticSilentRenew hasn't fired yet) sends
  // an expired token on the first API call, which the backend correctly
  // rejects with 401 — and nothing downstream retries with a fresh token.
  private async getFreshUser(staleUser: User): Promise<User | null> {
    console.warn('⚠️ OIDC token expired — attempting silent renewal before use');
    try {
      const renewed = await this.userManager.signinSilent();
      if (renewed && !renewed.expired) {
        this.currentUser = renewed;
        console.log('✅ OIDC silent renewal succeeded');
        return renewed;
      }
      console.warn('⚠️ OIDC silent renewal returned no usable user, falling back to stale token');
    } catch (error) {
      console.error('❌ OIDC silent renewal failed, falling back to stale token', error);
    }
    return staleUser;
  }
  
  // Refresh token silently
  async refreshToken(): Promise<User | null> {
    try {
      console.log('OIDC: Refreshing token silently');
      const user = await this.userManager.signinSilent();
      this.currentUser = user;
      return user;
    } catch (error) {
      console.error('OIDC: Silent refresh error', error);
      return null;
    }
  }
  
  // Convert OIDC User to AuthUser format (for compatibility with existing code)
  convertToAuthUser(oidcUser: User): AuthUser {
    return {
      identityProvider: 'oidc',
      userId: oidcUser.profile.sub || '',
      userDetails: oidcUser.profile.email || oidcUser.profile.preferred_username || '',
      userRoles: this.extractRoles(oidcUser),
      claims: this.extractClaims(oidcUser)
    };
  }
  
  // Extract roles from OIDC user profile
  private extractRoles(user: User): string[] {
    const roles: string[] = ['authenticated']; // Default role
    
    // DEBUG: Log FULL profile to diagnose missing roles in lab environment
    console.log('🔐 [OIDC] ========== FULL TOKEN PROFILE ==========');
    console.log('🔐 [OIDC] Profile keys:', Object.keys(user.profile));
    console.log('🔐 [OIDC] Full profile:', JSON.stringify(user.profile, null, 2));
    console.log('🔐 [OIDC] ==========================================');
    
    // Check various possible role claims
    const roleClaims = [
      user.profile.roles,
      user.profile.role,
      user.profile.groups,
      user.profile.group,
      user.profile['custom:roles'],
      user.profile['custom:role'],
      // Azure AD specific claims
      (user.profile as any)['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
      (user.profile as any)['wids'], // Azure AD directory roles
    ];
    
    console.log('🔐 [OIDC] Role claims check:', {
      'roles': user.profile.roles,
      'role': user.profile.role,
      'groups': user.profile.groups,
      'wids': (user.profile as any)['wids'],
      'msRole': (user.profile as any)['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
    });
    
    for (const claim of roleClaims) {
      if (claim) {
        if (Array.isArray(claim)) {
          roles.push(...claim);
        } else if (typeof claim === 'string') {
          roles.push(claim);
        }
      }
    }
    
    const uniqueRoles = Array.from(new Set(roles)); // Remove duplicates
    console.log('🔐 [OIDC] Final extracted roles:', uniqueRoles);
    
    return uniqueRoles;
  }
  
  // Extract claims from OIDC user profile
  private extractClaims(user: User): Array<{ typ: string; val: string }> {
    const claims: Array<{ typ: string; val: string }> = [];
    
    // Convert profile properties to claims format
    Object.entries(user.profile).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        claims.push({
          typ: key,
          val: Array.isArray(value) ? value.join(',') : String(value)
        });
      }
    });
    
    return claims;
  }
  
  // Handle silent callback (for token renewal)
  async handleSilentCallback(): Promise<void> {
    try {
      console.log('OIDC: Handling silent callback');
      await this.userManager.signinSilentCallback();
      console.log('OIDC: Silent callback successful');
    } catch (error) {
      console.error('OIDC: Silent callback error', error);
    }
  }
}

export default OidcAuthService;
